/**
 * Returns exactly `JSON.stringify(a) === JSON.stringify(b)`, without building the strings.
 *
 * Follows JSON.stringify's rules: `toJSON(key)` is honored (once per value), boxed primitives are
 * unwrapped, object keys whose value is undefined/function/symbol are skipped (in arrays they become
 * null), NaN/±Infinity become null, key order matters, and objects without own enumerable keys
 * (Map, Set, RegExp, File, ...) serialize as `{}`.
 *
 * It is faster than serializing because it stops at the first difference and skips subtrees that are
 * the same reference on both sides. The one intended difference: it may return a result where
 * JSON.stringify would throw (a cycle or BigInt inside a subtree that is shared by reference, or
 * located after the first difference).
 */
export function jsonEqual(a: unknown, b: unknown): boolean {
	if (a === b) return true;
	return equalNormalized(normalize(a, ""), normalize(b, ""), 0);
}

/** Beyond this depth, fall back to JSON.stringify (which throws on cycles, as before). */
const MAX_DEPTH = 1000;

type JsonKind = "omit" | "null" | "number" | "string" | "boolean" | "array" | "object";

function normalize(value: any, key: string): any {
	const type = typeof value;
	if (
		value !== null &&
		(type === "object" || type === "function" || type === "bigint") &&
		typeof value.toJSON === "function"
	) {
		value = value.toJSON(key);
	}
	if (value !== null && typeof value === "object") {
		if (value instanceof Number) return Number(value);
		if (value instanceof String) return String(value);
		if (value instanceof Boolean) return value.valueOf();
		if (value instanceof BigInt) return value.valueOf();
	}
	return value;
}

function kindOf(value: any): JsonKind {
	switch (typeof value) {
		case "undefined":
		case "function":
		case "symbol":
			return "omit";
		case "number":
			return Number.isFinite(value) ? "number" : "null";
		case "string":
			return "string";
		case "boolean":
			return "boolean";
		case "bigint":
			throw new TypeError("Do not know how to serialize a BigInt");
		default:
			if (value === null) return "null";
			return Array.isArray(value) ? "array" : "object";
	}
}

function equalNormalized(a: any, b: any, depth: number): boolean {
	if (a === b) return true;
	const kind = kindOf(a);
	if (kind !== kindOf(b)) return false;
	switch (kind) {
		case "omit":
		case "null":
			return true;
		case "number":
		case "string":
		case "boolean":
			return a === b;
		case "array":
			if (depth > MAX_DEPTH) return JSON.stringify(a) === JSON.stringify(b);
			return arraysEqual(a, b, depth);
		default:
			if (depth > MAX_DEPTH) return JSON.stringify(a) === JSON.stringify(b);
			return objectsEqual(a, b, depth);
	}
}

function arraysEqual(a: any[], b: any[], depth: number) {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		// Same raw element => identical serialization
		if (a[i] === b[i]) continue;
		const key = String(i);
		let itemA = normalize(a[i], key);
		let itemB = normalize(b[i], key);
		if (kindOf(itemA) === "omit") itemA = null;
		if (kindOf(itemB) === "omit") itemB = null;
		if (!equalNormalized(itemA, itemB, depth + 1)) return false;
	}
	return true;
}

function objectsEqual(a: object, b: object, depth: number) {
	const keysA = Object.keys(a);
	const keysB = Object.keys(b);
	let i = 0;
	let j = 0;
	for (;;) {
		// Same key holding the same raw value => both sides serialize (or are skipped) identically
		if (i < keysA.length && j < keysB.length && keysA[i] === keysB[j] && a[keysA[i]] === b[keysB[j]]) {
			i++;
			j++;
			continue;
		}
		let valueA: any;
		let valueB: any;
		while (i < keysA.length) {
			valueA = normalize(a[keysA[i]], keysA[i]);
			if (kindOf(valueA) !== "omit") break;
			i++;
		}
		while (j < keysB.length) {
			valueB = normalize(b[keysB[j]], keysB[j]);
			if (kindOf(valueB) !== "omit") break;
			j++;
		}
		const doneA = i >= keysA.length;
		const doneB = j >= keysB.length;
		if (doneA || doneB) return doneA && doneB;
		if (keysA[i] !== keysB[j]) return false;
		if (!equalNormalized(valueA, valueB, depth + 1)) return false;
		i++;
		j++;
	}
}
